import { apiService } from '../../../../components/api/apiService';
import { logDataModels } from '../dataModelsLogger';
import type { PolicyAsset, PolicyType } from './types';

const CONNECTION = 'backend_py';

export type ExpressionCopilotAsset = {
  name: string;
  path: string;
  classification?: string | null;
  columns: Array<{
    name: string;
    type: string;
    classification?: string | null;
  }>;
};

export type ExpressionCopilotResponse = {
  expression: string;
  explanation: string;
  model: string;
  llmId: string;
};

function copilotErrorMessage(err: unknown): string {
  const axiosErr = err as {
    response?: { data?: { error?: { message?: string }; message?: string } };
    message?: string;
  };
  return (
    axiosErr.response?.data?.error?.message
    || axiosErr.response?.data?.message
    || axiosErr.message
    || 'Copilot request failed'
  );
}

export function serializeExpressionAssets(assets: PolicyAsset[]): ExpressionCopilotAsset[] {
  return assets.map((asset) => ({
    name: asset.name,
    path: asset.path || '',
    classification: asset.classification || null,
    columns: asset.columns
      .filter((column) => column.name)
      .map((column) => ({
        name: column.name,
        type: column.type || '',
        classification: column.classification || null,
      })),
  }));
}

export const copilotExpressionService = {
  async suggest(payload: {
    prompt: string;
    policyType: PolicyType;
    currentExpression?: string;
    priorExpression?: string;
    assets: PolicyAsset[];
  }): Promise<ExpressionCopilotResponse> {
    const assets = serializeExpressionAssets(payload.assets);
    logDataModels('app', 'governance_expression_copilot_requested', {
      policyType: payload.policyType,
      assets: assets.length,
      columns: assets.reduce((count, asset) => count + asset.columns.length, 0),
      hasPrior: Boolean(payload.priorExpression?.trim()),
    });
    try {
      const data = (await apiService.post(
        '/api/v1/copilot/expression',
        {
          prompt: payload.prompt.trim(),
          policyType: payload.policyType,
          currentExpression: payload.currentExpression || '',
          priorExpression: payload.priorExpression || undefined,
          assets,
        },
        CONNECTION,
      )) as Record<string, unknown>;
      const mapped: ExpressionCopilotResponse = {
        expression: String(data.expression ?? '').trim(),
        explanation: String(data.explanation ?? '').trim(),
        model: String(data.model ?? ''),
        llmId: String(data.llmId ?? data.llm_id ?? ''),
      };
      logDataModels('app', 'governance_expression_copilot_ok', {
        llmId: mapped.llmId,
        expressionChars: mapped.expression.length,
      });
      return mapped;
    } catch (err) {
      const message = copilotErrorMessage(err);
      logDataModels('error', 'governance_expression_copilot_failed', { message });
      throw new Error(message);
    }
  },
};
